import React from "react";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import { getStudyMaterialLink, studyMaterials } from "../../consts/studyMaterials";
import styles from "./studyMaterialsPage.module.scss";

function BookCover({ material }: { material: (typeof studyMaterials)[number] }) {
    if (material.coverImage) {
        return (
            <img
                className={styles.bookCoverImage}
                src={material.coverImage.src}
                alt={material.coverImage.alt}
                loading="lazy"
            />
        );
    }

    return (
        <div className={styles.bookCover} role="img" aria-label={`Illustrated placeholder cover for ${material.title}`}>
            <span className={styles.coverTurtle} aria-hidden="true">🐢</span>
            <span className={styles.coverSeries}>GENKI I</span>
            <span className={styles.coverType}>{material.type}</span>
        </div>
    );
}

function StudyMaterialsPage() {
    return (
        <div className={styles.page}>
            <Seo
                title="Recommended Study Materials"
                description="Trusted Japanese study materials recommended for NihongoTech learners, starting with Genki I."
                path="/study-materials"
            />
            <Header />

            <main id="main-content" className={styles.main}>
                <section className={styles.container} aria-labelledby="study-materials-title">
                    <p className={styles.eyebrow}>Study guidance</p>
                    <h1 id="study-materials-title" className={styles.title}>Recommended Study Materials</h1>
                    <p className={styles.introduction}>
                        These are learning resources I trust and use in teaching. This page is a study guide, not a shop: every recommendation is here because it supports steady, understandable Japanese learning.
                    </p>
                    <aside className={styles.teacherNote} aria-label="Why Genki is recommended">
                        <span className={styles.noteTurtle} aria-hidden="true">🐢</span>
                        <p>
                            Genki is the book I personally use in my online Japanese classes, and it was recommended by my teacher. It gives beginners a dependable path through grammar, vocabulary, and practice.
                        </p>
                    </aside>

                    <div className={styles.materialList}>
                        {studyMaterials.map((material) => {
                            const link = getStudyMaterialLink(material, "materialsPage");

                            return (
                                <article key={material.id} className={styles.materialCard}>
                                    <BookCover material={material} />
                                    <div className={styles.cardContent}>
                                        <p className={styles.resourceType}>{material.type}</p>
                                        <h2 className={styles.materialTitle}>{material.title}</h2>
                                        <p className={styles.description}>{material.description}</p>
                                        <dl className={styles.details}>
                                            <div>
                                                <dt>Suitable for</dt>
                                                <dd>{material.suitableFor}</dd>
                                            </div>
                                            <div>
                                                <dt>Why it is recommended</dt>
                                                <dd>{material.recommendation}</dd>
                                            </div>
                                        </dl>
                                        {material.details && (
                                            <dl className={styles.bookDetails} aria-label={`${material.title} edition details`}>
                                                {material.details.map((detail) => (
                                                    <div key={detail.label}>
                                                        <dt>{detail.label}</dt>
                                                        <dd>{detail.value}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        )}
                                        {link.href ? (
                                            <>
                                                <a
                                                    className={styles.action}
                                                    href={link.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-tracking-id={link.trackingId || undefined}
                                                >
                                                    View book<span className={styles.actionArrow} aria-hidden="true">→</span>
                                                </a>
                                                {link.isAffiliate && (
                                                    <p className={styles.affiliateDisclosure}>
                                                        Affiliate link: NihongoTech may earn a small commission if you buy through this link, at no extra cost to you.
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <button type="button" className={styles.action} disabled title="A verified book link has not been added yet.">
                                                View book <span className={styles.comingSoon}>— link coming soon</span>
                                            </button>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default StudyMaterialsPage;
